import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import Datetime from 'react-datetime';
import moment from 'moment';
import Header from 'components/Header';
import Footer from 'components/Footer';
import FormGroup from 'components/Form/FormGroup';
import Input from 'components/Form/Input';
import FormGroupLabel from 'components/Form/FormGroupLabel';
import { Grid, Column } from 'components/Layout';
import { Button, ButtonLink } from 'components/Button';
import Checkbox from 'components/Form/Checkbox';
import Select from 'components/Form/Select';
import Validator from 'components/Validator/Validator';
import { get } from 'lodash';
import { PuffLoader } from 'react-spinners';
import { getAutopayDraftsAction, nextAction } from 'actions/workflow';
import { getOrConvertToPst } from 'utils/timezone';
import { formatCurrency } from 'utils/formatCurrency';
import schema, {
  getFirstDraftDateBounds,
  isFirstDraftDateInRange,
} from './schema';
import paymentDateSchema from './paymentDateSchema';
import editIcon from '../../../assets/images/edit.png';
import cancelEditIcon from '../../../assets/images/cancel-edit.png';
import style from './style.scss';
import { accountMask } from '../../../utils/masks';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { ToastProvider, useToast } from 'components/BankStatements/ui/toast';

const AUTOPAY_FREQUENCY_OPTIONS = [
  { value: 'monthly', title: 'Monthly' },
  { value: 'bi-weekly', title: 'Every 2 Weeks' },
  { value: 'weekly', title: 'Weekly' },
];

const isAutopayDraftRequestReady = (frequency, draftDate, draftDay, isDraftDateValid) => {
  if (!frequency) {
    return false;
  }

  if (frequency === 'monthly') {
    return isDraftDateValid && !!draftDate;
  }

  return !!draftDay;
};

const DRAFT_DAY_OPTIONS = [
  { value: 'Monday', title: 'Monday' },
  { value: 'Tuesday', title: 'Tuesday' },
  { value: 'Wednesday', title: 'Wednesday' },
  { value: 'Thursday', title: 'Thursday' },
  { value: 'Friday', title: 'Friday' },
];

const getDefaultFirstPaymentDate = () => getOrConvertToPst().add(35, 'd').format('MM/DD/YYYY');

const getLastDraftPaymentDate = (drafts) => {
  if (!drafts || drafts.length === 0) return null;

  const lastDraft = drafts[drafts.length - 1];
  const parsedDate = getOrConvertToPst(lastDraft.draftDate);
  return parsedDate.isValid() ? parsedDate.format('MM/DD/YYYY') : null;
};

const AutoPayElection = (props) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const dateTimeRef = useRef(null);
  const draftDateTimeRef = useRef(null);
  const firstPaymentDateRef = useRef(null);
  const firstDraftDateRef = useRef(null);
  const initializedRef = useRef(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [dateFocused, setDateFocused] = useState(false);
  const [isValidFirstPaymentDate, setIsValidFirstPaymentDate] = useState(false);
  const [isGiactPopupVisible, setIsGiactPopupVisible] = useState(false);
  const [giactPopupAttrs, setGiactPopupAttrs] = useState(null);
  const [giactMessage, setGiactMessage] = useState(null);
  const [hasCompletedPlaid, setHasCompletedPlaid] = useState(false);
  const [isBankAccountExisting, setIsBankAccountExisting] = useState(false);
  const [isValidFirstDraftDate, setIsValidFirstDraftDate] = useState(false);
  const [firstDraftDateFocused, setFirstDraftDateFocused] = useState(false);
  const [autopayDrafts, setAutopayDrafts] = useState([]);
  const [isAutopayDraftsLoading, setIsAutopayDraftsLoading] = useState(false);
  const [autopayDraftsError, setAutopayDraftsError] = useState(null);
  const applicationId = useSelector((state) =>
    get(state, 'workflow.data.data.applicationId')
    || get(state, 'workflow.attributes.entityId')
    || ''
  );
  const data = useSelector((state) => get(state, 'workflow.data.data') || get(state, 'workflow.state.data'));
  const { validator: { values, errors, isValid } } = props;
  const isMonthlyAutopay = values.autopayFrequency === 'monthly';
  const isBiWeeklyAutopay = values.autopayFrequency === 'bi-weekly';
  const isWeeklyAutopay = values.autopayFrequency === 'weekly';
  const autopayDraftsLoader = (
    <div className={style.draftScheduleLoader}>
      <PuffLoader color="#3989E3" size={50} />
    </div>
  );

  useEffect(() => {
    const { validator: { setValues } } = props;
    if (!key) {
      navigate('/fsl/application', { replace: true });
      return;
    }

    if (!data) {
      navigate(`/applications/fsl/checkin?key=${key}`)
      return;
    }

    setHasCompletedPlaid(get(data, 'hasCompletedPlaid', false));
    const accounts = get(data, 'bankAccounts');
    const primaryAccountIndex = (accounts || []).findIndex(acc => acc.isPrimary);
    const bankData = accounts ? accounts[primaryAccountIndex === -1 ? 0 : primaryAccountIndex] : undefined;
    // Check if checkin API returned autopay pre-fill data
    const checkinAutopayFrequency = get(data, 'AutopayFrequency') || get(data, 'autopayFrequency');
    const checkinAutopayDrafts = get(data, 'AutopayDrafts') || get(data, 'autopayDrafts');

    // Derive draft day from the first draft's date for bi-weekly/weekly
    let prefillDraftDay = '';
    let prefillFirstDraftDate = '';
    if (checkinAutopayFrequency && checkinAutopayDrafts && checkinAutopayDrafts.length > 0) {
      const [firstDraft] = checkinAutopayDrafts;
      const draftDateTime = get(firstDraft, 'DraftDate.DateTime') || get(firstDraft, 'draftDate.dateTime') || get(firstDraft, 'draftDate');
      if (draftDateTime) {
        const parsedDate = getOrConvertToPst(draftDateTime);
        if (parsedDate.isValid()) {
          if (checkinAutopayFrequency === 'bi-weekly' || checkinAutopayFrequency === 'weekly') {
            prefillDraftDay = parsedDate.format('dddd');
          } else if (checkinAutopayFrequency === 'monthly') {
            prefillFirstDraftDate = parsedDate.format('MM/DD/YYYY');
          }
        }
      }
    }

    const initialBankData = {
      accounts,
      accountId: get(bankData, 'accountId'),
      accountHolderName: get(bankData, 'accountHolderName'),
      bankName: get(bankData, 'bankName'),
      routingNumber: get(bankData, 'routingNumber'),
      accountNumber: get(bankData, 'accountNumber'),
      accountType: get(bankData, 'accountType'),
      isEditingBankInfo: false,
      firstPaymentDate: getDefaultFirstPaymentDate(),
      autopayFrequency: checkinAutopayFrequency || null,
      firstDraftDate: prefillFirstDraftDate,
      draftDay: prefillDraftDay,
      isSelfSubmitted: checkinAutopayFrequency ? 1 : undefined,
    };
    setValues(initialBankData);

    // If checkin returned autopay drafts, pre-populate the draft table and mark dates valid
    if (checkinAutopayFrequency && checkinAutopayDrafts && checkinAutopayDrafts.length > 0) {
      const normalizedDrafts = checkinAutopayDrafts.map((draft) => {
        const draftDateTime = get(draft, 'DraftDate.DateTime') || get(draft, 'draftDate.dateTime') || get(draft, 'draftDate');
        return {
          draftDate: draftDateTime,
          draftDateRaw: draftDateTime,
          amount: get(draft, 'Amount') || get(draft, 'amount'),
        };
      });
      setAutopayDrafts(normalizedDrafts);

      if (checkinAutopayFrequency === 'monthly' && prefillFirstDraftDate) {
        setIsValidFirstDraftDate(true);
      }

      // Derive firstPaymentDate from last draft
      const lastDraftDate = getLastDraftPaymentDate(normalizedDrafts);
      if (lastDraftDate) {
        setIsValidFirstPaymentDate(true);
      }
    } else {
      setIsValidFirstDraftDate(false);
    }

    if (accounts && accounts.length > 0) {
      setIsBankAccountExisting(true);
      setIsPreloaded(true);
    } else {
      setIsBankAccountExisting(false);
    }
    initializedRef.current = true;
  }, [key, data, navigate]);

  const fetchAutopayDrafts = (frequency, draftDate, draftDay) => {
    if (!key || !applicationId || !frequency) {
      return;
    }

    let params = '';
    if (frequency === 'monthly') {
      const parsedDraftDate = moment(draftDate, 'MM/DD/YYYY', true);
      if (!parsedDraftDate.isValid()) {
        return;
      }
      params = `?FirstPaymentDate=${parsedDraftDate.format('YYYY-MM-DD')}&AutopayFrequency=monthly`;
    } else if (frequency === 'bi-weekly' || frequency === 'weekly') {
      params = `?day=${draftDay}&AutopayFrequency=${frequency}`;
    }

    setIsAutopayDraftsLoading(true);
    setAutopayDraftsError(null);
    dispatch(getAutopayDraftsAction({
      url: `/workflows/fsl/application/${applicationId}/workflow/fsl2/${key}/autopay-drafts${params}`,
      success: (response) => {
        const drafts = response.autopayDrafts || [];
        setAutopayDrafts(drafts);
        setAutopayDraftsError(null);
        setIsAutopayDraftsLoading(false);

        const lastDraftDate = getLastDraftPaymentDate(drafts);
        if (lastDraftDate) {
          const { validator: { setValues } } = props;
          setValues({
            ...values,
            firstPaymentDate: lastDraftDate,
          });
          setIsValidFirstPaymentDate(true);
        }
      },
      fail: (error) => {
        const errorMessage = getApiErrorMessage(error, 'Unable to load draft dates.');
        setAutopayDrafts([]);
        setAutopayDraftsError(errorMessage);
        setIsAutopayDraftsLoading(false);
      },
    }));
  };

  // Track whether user has manually changed autopay settings after initial pre-fill
  const userChangedAutopayRef = useRef(false);

  useEffect(() => {
    // Don't run this effect until initialization is complete
    if (!initializedRef.current) {
      return;
    }

    // If user hasn't changed anything yet and we already have pre-filled drafts, don't fetch
    if (!userChangedAutopayRef.current && autopayDrafts.length > 0) {
      return;
    }

    if (values.isSelfSubmitted !== 1 || !key || !applicationId) {
      setAutopayDrafts([]);
      return;
    }

    if (!isAutopayDraftRequestReady(
      values.autopayFrequency,
      values.firstDraftDate,
      values.draftDay,
      isValidFirstDraftDate
    )) {
      setAutopayDrafts([]);
      return;
    }

    fetchAutopayDrafts(
      values.autopayFrequency,
      values.firstDraftDate,
      values.draftDay
    );
  }, [
    values.isSelfSubmitted,
    values.autopayFrequency,
    values.firstDraftDate,
    values.draftDay,
    isValidFirstDraftDate,
    key,
    applicationId,
  ]);

  const handleCheckboxChange = (name, value) => {
    const { validator: { onChangeHandler, setValues, deleteErrors, values: formValues } } = props;

    userChangedAutopayRef.current = true;
    setDateFocused(false);
    setFirstDraftDateFocused(false);
    setIsValidFirstDraftDate(false);
    setAutopayDrafts([]);
    setAutopayDraftsError(null);

    if (name === 'isSelfSubmitted' && value === 1) {
      deleteErrors(['firstPaymentDate']);
      setIsValidFirstPaymentDate(true);
      setValues({
        ...formValues,
        isSelfSubmitted: 1,
        autopayFrequency: formValues.autopayFrequency || null,
        firstDraftDate: '',
        firstPaymentDate: getDefaultFirstPaymentDate(),
      });
      return;
    }

    if (name === 'isSelfSubmitted' && value !== 1) {
      setValues({
        ...formValues,
        isSelfSubmitted: value,
        autopayFrequency: null,
        firstDraftDate: '',
        draftDay: '',
      });
      return;
    }

    onChangeHandler(name, value);
  };

  const handleDateChange = (date) => {
    const { validator: { onChangeHandler } } = props;
    if (typeof date.format === 'function') {
      onChangeHandler('firstPaymentDate', date.format('MM/DD/YYYY'));
      setIsValidFirstPaymentDate(date.isAfter(getOrConvertToPst().add(34, 'd')) &&
      date.isBefore(getOrConvertToPst().add(45, 'd')));
    } else {
      onChangeHandler('firstPaymentDate', date);
      setIsValidFirstPaymentDate(false);
    }
  };

  const handleDraftDateChange = (date) => {
    const { validator: { onChangeHandler } } = props;
    userChangedAutopayRef.current = true;
    setAutopayDrafts([]);
    setAutopayDraftsError(null);
    if (typeof date.format === 'function') {
      onChangeHandler('firstDraftDate', date.format('MM/DD/YYYY'));
      setIsValidFirstDraftDate(isFirstDraftDateInRange(date));
    } else {
      onChangeHandler('firstDraftDate', date);
      setIsValidFirstDraftDate(false);
    }
  };

  const handleAutopayFrequencyChange = (event) => {
    const { validator: { setValues, values: formValues } } = props;
    const frequency = event.target.value || null;
    userChangedAutopayRef.current = true;
    setValues({
      ...formValues,
      autopayFrequency: frequency,
      firstDraftDate: '',
      draftDay: '',
    });
    setIsValidFirstDraftDate(false);
    setAutopayDrafts([]);
    setAutopayDraftsError(null);
  };

  const handleDateFocus = (e, fn) => {
    fn(e);
    setDateFocused(true);
  };

  const handleDraftDateFocus = (e, fn) => {
    fn(e);
    setFirstDraftDateFocused(true);
  };

  const prefillBankAccount = (accountIndex, isEditingBankInfo) => {
    const { validator: { setValues, values } } = props;
    const accounts = get(values, 'accounts', []);
    const bankData = accounts.find(acc => acc.accountId === accountIndex);

    if (bankData) {
      setValues({
        ...values,
        accountId: accountIndex,
        accountHolderName: get(bankData, 'accountHolderName') || '',
        bankName: get(bankData, 'bankName') || '',
        routingNumber: get(bankData, 'routingNumber') || '',
        accountNumber: get(bankData, 'accountNumber') || '',
        accountType: get(bankData, 'accountType') || '',
        isEditingBankInfo,
      });
    }
  };

  const handleDraftDayChange = (event) => {
    const draftDay = event.target.value;
    const { validator: { setValues, values: formValues } } = props;
    userChangedAutopayRef.current = true;
    setAutopayDrafts([]);
    setAutopayDraftsError(null);

    setValues({
      ...formValues,
      draftDay,
      firstDraftDate: '',
    });
  };

  const handleInputChange = (event) => {
    const { validator: { values, onChangeHandler } } = props;
    if (event.target.name === 'accountId') {
      prefillBankAccount(event.target.value, values.isEditingBankInfo);
    } else if (event.target.name === 'draftDay') {
      handleDraftDayChange(event);
    } else if (event.target.name === 'firstDraftDate') {
      handleDraftDateChange(event.target.value);
    } else if (event.target.name === 'firstPaymentDate') {
      handleDateChange(event.target.value);
    } else {
      onChangeHandler(event.target.name, event.target.value);
    }
  };

  const toggleGiactPopup = (e) => {
    e.preventDefault();
    setIsGiactPopupVisible(!isGiactPopupVisible);
  };

  const handleButtonClick = (e, flag = false) => {
    e.preventDefault();
  
    setIsSubmitted(true);
    if (key) {
      const { validator: { values, validate, errors, setErrors } } = props;
      const validateSchema = values.isSelfSubmitted === 1 ? schema : (values.isSelfSubmitted === 2 && paymentDateSchema);
      if (validateSchema && validate(validateSchema).isValid) {
        setIsLoading(true);

        const datePattern = 'MM/DD/YYYY';

        const payloadData = {
          IsAutoPay: flag ? false : values.isSelfSubmitted === 1,
          firstPaymentDate: moment(values.firstPaymentDate).format(datePattern),
          MerchantName: 'LendingUSA',
        };

        if ((!isBankAccountExisting || values.isEditingBankInfo) && !flag) {
          payloadData.BankName = values.bankName || '';
          payloadData.AccountHolderName = values.accountHolderName || '';
          payloadData.RoutingNumber = values.routingNumber || '';
          payloadData.AccountNumber = values.accountNumber || '';
          payloadData.AccountType = values.accountType || '';
        } else if (isBankAccountExisting && !values.isEditingBankInfo) {
          payloadData.accountId = values.accountId;
        }

        if (!flag && values.isSelfSubmitted === 1) {
          payloadData.AutopayFrequency = values.autopayFrequency;
          if (values.autopayFrequency === 'monthly') {
            payloadData.FirstPaymentDate = moment(values.firstDraftDate, 'MM/DD/YYYY').format(datePattern);
            payloadData.AutopayDrafts = autopayDrafts.map(draft => ({
              draftDate: draft.draftDateRaw || draft.draftDate,
              amount: draft.amount,
            }));
          } else if (values.autopayFrequency === 'bi-weekly' || values.autopayFrequency === 'weekly') {
            payloadData.AutopayDrafts = autopayDrafts
          }
        }

        dispatch(nextAction({
          data: payloadData,
          url: `/workflows/fsl/${key}/next`,
          success: (response) => {
            const routeUrl = response.data && response.data.url;
            navigate(routeUrl);
          },
          fail: (error) => {
            setIsLoading(false);
            const failureError = get(error, 'data.failure.errors.0');

            if (get(failureError, 'subject') === 'bankAccountVerificationFailed') {
              setIsGiactPopupVisible(true);
              setGiactPopupAttrs(get(failureError, 'attributes'));
              setGiactMessage(getApiErrorMessage(failureError || error, 'Unable to sign up for AutoPay.'));
            } else if (get(failureError, 'subject') === 'date') {
              setErrors({
                ...errors,
                firstPaymentDate: getApiErrorMessage(failureError || error, 'Please enter a valid payment date.'),
              });
            } else {
              showToast(getApiErrorMessage(failureError || error, 'Something went wrong. Please try again.'), 'error');
            }
          },
        }));
      } else {
        validate(validateSchema || schema);
      }
    }
  };

  const togglePopup = () => {
    setIsPopupVisible(!isPopupVisible);
  };

  if (!isValid && isSubmitted) {
    const errs = Object.keys(errors);
    if (errs[0] === 'firstPaymentDate' && firstPaymentDateRef.current) {
      window.scrollTo(0, firstPaymentDateRef.current.offsetTop - window.innerHeight / 2 + 44);
    } else if (errs[0] === 'firstDraftDate' && firstDraftDateRef.current) {
      window.scrollTo(0, firstDraftDateRef.current.offsetTop - window.innerHeight / 2 + 44);
    } else {
      const [element] = window.document.getElementsByName(errs[0]);
      if (element) {
        element.focus();
      }
    }
    setIsSubmitted(false);
  }

  if (dateFocused) {
    const [paymentDateInput] = document.getElementsByName('firstPaymentDate');
    if (paymentDateInput) {
      paymentDateInput.placeholder = 'MM/DD/YYYY';
    }
  }


  const toggleBankInfoEdit = () => {
    const { validator: { setValues } } = props;

    if (!values.isEditingBankInfo) {
      setValues({
        ...values,
        accountNumber: '',
        routingNumber: '',
        accountType: 'Checking',
        accountHolderName: '',
        bankName: '',
        isEditingBankInfo: !values.isEditingBankInfo,
      });
      setIsPreloaded(false);
    } else {
      prefillBankAccount(values.accountId, !values.isEditingBankInfo);
      setIsPreloaded(true);
    }
  };

  return (
    <Fragment>
      <Header />
      <section className="grid-container fluid">
        {hasCompletedPlaid ?
          <div className={cn('grid-container fluid', style.autoPayHeader)} />
        : null}
        <div className="grid-container fluid borrowers-apply page-autopay">
          <div className="grid-container">
            <Grid className="grid-margin-x max-limited">
              {applicationId ? (
                <div className="cell small-12 large-8 large-offset-2">
                  <p className="p-large"><b>Application ID:</b> {applicationId}</p>
                </div>
              ) : null}
              <Column className={cn('small-12 large-8 large-offset-2 form-headline', style.formHeadline)}>
                <h2>Auto-Pay Election</h2>
              </Column>
              <Column className="small-12 large-8 large-offset-2">
                <FormGroup className="form-group position-relative">
                  <div style={{ position: 'relative' }} className="bankAchHeader">
                    <FormGroupLabel value="Bank Account Information" />
                    {isBankAccountExisting && (
                    <div style={{ position: 'absolute', top: 0, right: 0 }} onClick={toggleBankInfoEdit}>
                      <img src={values.isEditingBankInfo ? cancelEditIcon : editIcon} alt={values.isEditingDebitCard ? 'edit icon' : 'cancel edit icon'} />
                    </div>
                  )}
                    <Grid className="grid-margin-x">
                      {values.accounts && values.accounts.length > 1 && !values.isEditingBankInfo ? (
                        <Column className="small-12">
                          <div className="form-box-row">
                            <Select
                              data={values.accounts.map(acc => ({
                                value: acc.accountId,
                                title: `${acc.accountType} #${acc.accountNumber.slice(-4)}`,
                              }))}
                              label="Pay from Account"
                              placeholder="Select..."
                              name="accountId"
                              onChange={handleInputChange}
                              isRequired
                              isErrorVisible={false}
                              value={values.accountId}
                              showSelectOption={false}
                            />
                          </div>
                        </Column>
                      ) : null}
                      <Column className="small-12 medium-6">
                        <Input
                          label="Bank Name"
                          isRequired
                          value={values.bankName}
                          onChange={handleInputChange}
                          name="bankName"
                          hasError={!!errors.bankName}
                          errorMessage={errors.bankName}
                          readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                        />
                      </Column>
                      <Column className="small-12 medium-6">
                        <Input
                          name="accountHolderName"
                          label="Name on Account"
                          isRequired
                          value={values.accountHolderName}
                          onChange={handleInputChange}
                          hasError={!!errors.accountHolderName}
                          errorMessage={errors.accountHolderName}
                          isDisabled={isPreloaded}
                          maxLength={40}
                          readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                        />
                      </Column>
                      <Column className="small-12 medium-6">
                        <Input
                          name="routingNumber"
                          label="ABA Number"
                          onChange={handleInputChange}
                          isRequired
                          value={values.routingNumber}
                          isMasked={(values.isEditingBankInfo || !isBankAccountExisting) ? [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/] : undefined}
                          hasError={!!errors.routingNumber}
                          errorMessage={errors.routingNumber}
                          readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                        />
                      </Column>
                      <Column className="small-12 medium-6">
                        <Input
                          name="accountNumber"
                          label="Account Number"
                          onChange={handleInputChange}
                          isRequired
                          value={values.accountNumber}
                          type="tel"
                          isMasked={(values.isEditingBankInfo || !isBankAccountExisting) ? accountMask : undefined}
                          hasError={!!errors.accountNumber}
                          errorMessage={errors.accountNumber}
                          readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                        />
                      </Column>
                      <Column className="small-12 medium-6">
                        <Select
                          data={[
                          { value: 'Checking', title: 'Checking' },
                          { value: 'Savings', title: 'Savings' },
                        ]}
                          name="accountType"
                          label="Account Type"
                          placeholder="Select..."
                          onChange={handleInputChange}
                          isRequired
                          hasError={!!errors.accountType}
                          errorMessage={errors.accountType}
                          value={values.accountType}
                          readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                        />
                      </Column>
                    </Grid>
                  </div>
                </FormGroup>
                <FormGroup className="form-group">
                  <FormGroupLabel value="Enroll in Auto-Pay" />
                  <Grid>
                    <Column className="small-12 padded-bottom padded-bottom-small">
                      <div className={style.enrollOptions}>
                        <div className={cn(style['radio-container'], values.isSelfSubmitted === 1 && style['radio-container-selected'])}>
                          <Checkbox
                            label={[
                              <strong>YES</strong>,
                              <Fragment>  &nbsp;–&nbsp;  </Fragment>,
                              'I want to enroll in Auto-Pay and never miss a payment again!',
                              <Fragment> &nbsp;</Fragment>,
                              hasCompletedPlaid ?
                                <ButtonLink onClick={togglePopup} className={style.viewDetailCheckbox}>*View Details</ButtonLink>
                              : '',
                            ]}
                            className={style.checkboxLabel}
                            name="isSelfSubmitted"
                            onChange={handleCheckboxChange.bind(null, 'isSelfSubmitted', 1)}
                            isChecked={values.isSelfSubmitted === 1}
                            value="on"
                            id="isSelfSubmitted"
                            type="radio"
                          />
                          <small className={style.small}>Paying via Auto-Pay is free and assures you pay on time each month.</small>
                        </div>
                        <div className={cn(style['radio-container'], values.isSelfSubmitted === 2 && style['radio-container-selected'])}>
                          <Checkbox
                            label={[
                              <strong>NO</strong>,
                              <Fragment> &nbsp;-&nbsp; </Fragment>,
                              'I do not want to Enroll in ACH Auto-Pay at this time.',
                            ]}
                            className={style.checkboxLabel}
                            name="isSelfSubmitted"
                            onChange={handleCheckboxChange.bind(null, 'isSelfSubmitted', 2)}
                            isChecked={values.isSelfSubmitted === 2}
                            id="isSelfSubmittedNo"
                            value="off"
                            type="radio"
                          />
                        </div>
                        {errors.isSelfSubmitted && (
                          <span className={style.error}>{errors.isSelfSubmitted}</span>
                        )}
                        <p className={style.enrollPromoText}>
                          Enroll in optional Auto-Pay for free and never miss a payment again!
                          {hasCompletedPlaid ?
                            <Fragment>
                              {' '}
                              Plus, enroll and stay enrolled in Auto-Pay and you could earn a <strong>$25 Credit</strong> after other criteria is met!*&nbsp;
                              <ButtonLink onClick={togglePopup} className={style.viewDetail}>View Details</ButtonLink>
                            </Fragment>
                          : null}
                        </p>
                      </div>
                    </Column>
                  </Grid>
                </FormGroup>
                {values.isSelfSubmitted === 2 && (
                  <FormGroup className="form-group">
                    <FormGroupLabel value="Choose Your Loan Due Date" />
                    <p className="mb-1">This will be the date your loan payments are due to LendingUSA.</p>
                    <Grid>
                      <Column className="small-12 medium-6">
                        <div className="first-payment-date" ref={firstPaymentDateRef}>
                          <Datetime
                            ref={dateTimeRef}
                            value={values.firstPaymentDate}
                            defaultValue={getOrConvertToPst().add(35, 'd')}
                            onChange={handleDateChange}
                            closeOnSelect
                            dateFormat="MM/DD/YYYY"
                            displayTimeZone="America/Los_Angeles"
                            timeFormat={false}
                            isValidDate={current => (
                              current.isAfter(getOrConvertToPst().add(34, 'd')) &&
                                current.isBefore(getOrConvertToPst().add(45, 'd'))
                            )}
                            initialViewDate={getOrConvertToPst().add(35, 'd')}
                            renderInput={renderProps => (
                              <Input
                                name="firstPaymentDate"
                                label="Payment Date"
                                labelClassName={cn(
                                  dateFocused ? 'has-value' : '',
                                  { [style['iscompleted-data']]: !!isValidFirstPaymentDate }
                                )}
                                placeHolder="MM/DD/YYYY"
                                isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                isRequired
                                hasError={!!errors.firstPaymentDate}
                                errorMessage={errors.firstPaymentDate}
                                {...renderProps}
                                onChange={handleInputChange}
                                value={values.firstPaymentDate}
                                onFocus={e => (handleDateFocus(e, renderProps.onFocus))}
                                enableAutocomplete={false}
                              />
                            )}
                          />
                        </div>
                      </Column>
                    </Grid>
                  </FormGroup>
                )}
                {values.isSelfSubmitted === 1 && (
                  <FormGroup className="form-group">
                    <FormGroupLabel value="Choose Auto-Pay Frequency" />
                    <Grid className="grid-margin-x">
                      <Column className="small-12 medium-6">
                        <div className="form-box-row">
                          <Select
                            data={AUTOPAY_FREQUENCY_OPTIONS}
                            label="Payment Frequency"
                            placeholder="Select..."
                            name="autopayFrequency"
                            onChange={handleAutopayFrequencyChange}
                            isRequired
                            hasError={!!errors.autopayFrequency}
                            errorMessage={errors.autopayFrequency}
                            value={values.autopayFrequency}
                            showSelectOption
                          />
                        </div>
                      </Column>
                      <Column className="small-12 medium-6">
                        {values.autopayFrequency === 'weekly' || values.autopayFrequency === 'bi-weekly' ? (
                          <div className="form-box-row">
                            <Select
                              data={DRAFT_DAY_OPTIONS}
                              label="Draft Day"
                              placeholder="Select..."
                              name="draftDay"
                              onChange={handleDraftDayChange}
                              isRequired
                              hasError={!!errors.draftDay}
                              errorMessage={errors.draftDay}
                              value={values.draftDay}
                              showSelectOption
                            />
                          </div>
                        ) : isMonthlyAutopay && (
                          <div className="first-payment-date" ref={firstDraftDateRef}>
                            <Datetime
                              ref={draftDateTimeRef}
                              value={values.firstDraftDate}
                              defaultValue={getOrConvertToPst().add(35, 'd')}
                              onChange={handleDraftDateChange}
                              closeOnSelect
                              dateFormat="MM/DD/YYYY"
                              displayTimeZone="America/Los_Angeles"
                              timeFormat={false}
                              isValidDate={isFirstDraftDateInRange}
                              initialViewDate={getFirstDraftDateBounds().lowBound}
                              renderInput={renderProps => (
                                <Input
                                  name="firstDraftDate"
                                  label="First Draft Date"
                                  labelClassName={cn(
                                    firstDraftDateFocused ? 'has-value' : '',
                                    { [style['iscompleted-data']]: !!isValidFirstDraftDate }
                                  )}
                                  placeHolder="MM/DD/YYYY"
                                  isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                  isRequired
                                  hasError={!!errors.firstDraftDate}
                                  errorMessage={errors.firstDraftDate}
                                  {...renderProps}
                                  onChange={handleInputChange}
                                  value={values.firstDraftDate}
                                  onFocus={e => handleDraftDateFocus(e, renderProps.onFocus)}
                                  enableAutocomplete={false}
                                />
                              )}
                            />
                     
                          </div>
                        )}
                      </Column>
                      {isMonthlyAutopay && isValidFirstDraftDate && values.firstDraftDate && (
                        <Column className="small-12">
                          <div className={style.draftSchedule}>
                            <div className={style.draftScheduleHeader}>
                              <span>Draft Date</span>
                              <span>Est. Draft Amount</span>
                            </div>
                            {isAutopayDraftsLoading && autopayDraftsLoader}
                            {!isAutopayDraftsLoading && autopayDrafts.map((draft, autoPayDraftIndex) => (
                              <div className={style.draftScheduleRow} key={autoPayDraftIndex}>
                                <span>{draft.draftDate ? getOrConvertToPst(draft.draftDate).format('MM-DD-YYYY') : '—'}</span>
                                <span>{draft.amount ? `$${formatCurrency(draft.amount, 2)}` : '—'}</span>
                              </div>
                            ))}
                            {!isAutopayDraftsLoading && !autopayDraftsError && autopayDrafts.length === 0 && (
                              <p className={style.draftScheduleLoading}>No draft dates available.</p>
                            )}
                          </div>
                        </Column>
                      )}
                      {(isBiWeeklyAutopay || isWeeklyAutopay) && values.draftDay && (
                        <Column className="small-12">
                          <div className={style.draftSchedule}>
                            <div className={style.draftScheduleHeader}>
                              <span>Draft Date</span>
                              <span>Est. Draft Amount</span>
                            </div>
                            {isAutopayDraftsLoading && autopayDraftsLoader}
                            {!isAutopayDraftsLoading && autopayDrafts.map((draft, index) => (
                              <div className={style.draftScheduleRow} key={index}>
                                <span>{draft.draftDate ? getOrConvertToPst(draft.draftDate).format('MM-DD-YYYY') : '—'}</span>
                                <span>{draft.amount ? `$${formatCurrency(draft.amount, 2)}` : '—'}</span>
                              </div>
                            ))}
                            {!isAutopayDraftsLoading && autopayDraftsError && (
                              <p className={style.draftScheduleError} role="alert">{autopayDraftsError}</p>
                            )}
                            {!isAutopayDraftsLoading && !autopayDraftsError && autopayDrafts.length === 0 && (
                              <p className={style.draftScheduleLoading}>No draft dates available.</p>
                            )}
                          </div>
                        </Column>
                      )}
                    </Grid>
                  </FormGroup>
                )}
                <FormGroup className="form-group">
                  <Grid>
                    <Column className={cn('small-12 padded-top', style.formHeadline)}>
                      <p>
                        <em>Please Note:</em> Enrolling in Auto-Pay is optional. The Bank&apos;s credit decision is not dependent on whether you enroll in ACH Auto-Pay. You can change your method of payment at any time in the future.
                      </p>
                    </Column>
                    <Column className="small-12 large-12 padded-bottom padded-bottom-small">
                      <Button
                        className={cn('button large green w-100', style.button, isLoading ? '' : cn('arrow', style.arrow))}
                        onClick={handleButtonClick}
                        isLoading={isLoading}
                      >
                        Save & Continue
                      </Button>
                    </Column>
                  </Grid>
                </FormGroup>
              </Column>
            </Grid>
          </div>
        </div>
      </section>
      <Footer isVisible={false} />
      {/* <!-- Disclaimer Modal --> */}
      <div className={cn('reveal', style.reveal, !isPopupVisible && style.invisible)}>
        <div className={style['card-header']}>
          <h5>View Details</h5>
        </div>
        <p style={{ maxHeight: '400px', overflowY: 'auto' }}>
          *To qualify for the $25 Principal Reduction (“Principal Reduction”) Promotion (“Promotion”), you must: (i) verify and provide your banking information, for at least one banking account, by signing into your banking account through a specified third party service (“Instant Verification”), you will not qualify for the Promotion if you: (a) do not, or are unable to, Instantly Verify any of your banking information, or (b) upload, e-mail or otherwise electronically provide banking account statements without Instantly Verifying at least one banking account, or (c) mail or provide any paper banking account statements without Instantly Verifying at least one banking account; (ii) enroll in optional Auto-Pay (“Auto-Pay”) at the same time that you execute your loan agreement and remain continuously enrolled up until and through the due date of your 6th scheduled monthly loan payment obligation; (iii) remain continuously enrolled in paperless billing up until and through the due date of your 6th scheduled monthly loan payment obligation; (iv) fulfill your first 6 (six) scheduled monthly loan payment obligations in full and on time, any late, returned or missed payments will disqualify you for the Promotion; there is no grace period; and (v) not pay off your loan prior to the application of the Principal Reduction to your loan account. Once all requirements are met, the Principal Reduction will reflect on your next scheduled loan statement. The Principal Reduction will not count towards any monthly payment obligation, you must continue to make your monthly payments as scheduled. You understand that you are not required to enroll in Auto-Pay unless you want to take advantage of this promotional offer, and your willingness to enroll in Auto-Pay will not affect the lender’s credit decision regarding your loan application. Limit one (1) Principal Reduction per loan. Promotion cannot be transferred or assigned. Promotion cannot be combined, if you qualify for this Promotion and any other promotion, such as the ‘No Interest on Principal Option Promotion’, you will only receive the promotion that has the higher value. This Promotion is subject to change or discontinuation without notice.
        </p>
        <button className="close-button" data-close aria-label="Close reveal" type="button" onClick={togglePopup}>
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      {/* <!-- End Disclaimer Modal --> */}
      {/* Giact check result modal */}
      <div className={cn('reveal', style.reveal, !isGiactPopupVisible && style.invisible)}>
        <div className={style['card-header']}>
          <h5>Unable to signup for AutoPay</h5>
        </div>
        <div>
          <p className="text-center">
            {giactMessage}
          </p>
          {
              giactPopupAttrs && giactPopupAttrs.allowRetry && (
                <Button
                  className={cn('button danger w-100', style.button)}
                  style={{ marginTop: 10 }}
                  onClick={toggleGiactPopup}
                >
                  Try Again
                </Button>
              )
            }
          {
              giactPopupAttrs && giactPopupAttrs.allowToProceedWithoutAutopay && (
                <Button
                  className={cn('button w-100 mt-0', style.button, !giactPopupAttrs.allowRetry ? '' : 'green')}
                  onClick={e => handleButtonClick(e, true)}
                >
                  Proceed Without Signing Up For Autopay
                </Button>
              )
            }
        </div>

        <button className="close-button" data-close aria-label="Close reveal" type="button" onClick={toggleGiactPopup}>
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      {/* End Giact check result modal */}
    </Fragment>
  );
};

AutoPayElection.propTypes = {
  validator: PropTypes.object.isRequired,
};

const AutoPayElectionPage = (props) => (
  <ToastProvider>
    <AutoPayElection {...props} />
  </ToastProvider>
);

export default Validator(schema)(AutoPayElectionPage);
